import styled from '@emotion/styled';
import { ThemeProps } from '@theme/base';
import { mq } from '@styles/breakpoints';
import { Icon } from '@components/common/generic-icon';
import { ModalContainer } from '@styles/modal';
import { Icons } from '@model/common/icons';
import { TextMarkdown } from '@components/common/description';
import { Button, ButtonVariant, ButtonSize, ButtonColor } from '@components/material-ui';
import { useI18NextContext } from '@components/hooks';

/* ***************** *
 *       Types       *
 * ***************** */
export interface EPCModalProps {
  onDismiss: () => void;
  modalData: any;
}

/* ****************** *
 *       Styles       *
 * ****************** */
const Container: any = styled.div(() => ({
  alignSelf: 'flex-start',
  boxSizing: 'content-box',
  display: 'grid',
  gridTemplateColumns: '1fr',
  padding: 0,
  boxShadow: '0px 75px 150px 0px rgba(0, 0, 0, 0.75)',
  [mq.small]: {
    padding: 0
  },
  [mq.medium]: {
    alignSelf: 'center',
    gridTemplateColumns: '1fr 1fr',
    gridTemplateRows: '1fr',
    maxWidth: 1000
  }
}));

const ModalImg: any = styled.div(({ theme }: ThemeProps) => ({
  backgroundColor: theme.custom.colors.white,
  overflow: 'hidden'
}));

const SaleImage = styled.img({
  height: '100%',
  width: '100%'
});

const ModalContent: any = styled.div(({ theme }: ThemeProps) => ({
  padding: theme.custom.spacing.large,
  marginBottom: theme.custom.spacing.xLarge,
  display: 'flex',
  justifyContent: 'center',
  flexDirection: 'column',
  backgroundColor: theme.custom.colors.white,

  [mq.medium]: {
    marginBottom: 0,
    padding: theme.custom.spacing.large * 2
  }
}));

const Link = styled.a(({ theme }: ThemeProps) => ({
  color: theme.custom.colors.group4.base,
  cursor: 'pointer',
  textUnderlineOffset: 4
}));

const ArrowIcon = styled(Icon)(({ theme }: ThemeProps) => ({
  fontSize: '1.6rem',
  color: theme.custom.colors.group2.base
}));

const Dismiss = styled.p({
  textAlign: 'center'
});

export const EPCModal = ({ onDismiss, modalData }: EPCModalProps) => {
  const t: any = useI18NextContext();
  const { markdown, heroImage, id } = modalData;
  const imgById = {
    11: '/assets/images/promo/tour-tzoo-discount-500.jpg',
    15: '/assets/images/promo/tour-tzoo-discount-solo.jpg'
  };

  return (
    <ModalContainer>
      <Container>
        <ModalImg>
          <SaleImage src={heroImage || imgById[Number(id)] || '/assets/images/promo/tour-ppc-discount-400.jpg'} />
        </ModalImg>
        <ModalContent>
          <TextMarkdown description={markdown} />
          <div>
            <Button
              variant={ButtonVariant.OUTLINED}
              color={ButtonColor.PRIMARY}
              size={ButtonSize.SMALL}
              endIcon={<ArrowIcon name={Icons.ARROW_RIGHT} />}
              label={t('epc-modal__button--label')}
              onClick={onDismiss}
            />
            <Dismiss>
              <Link onClick={onDismiss} rel="noopener">
                {t('epc-modal__dismiss--label')}
              </Link>
            </Dismiss>
          </div>
        </ModalContent>
      </Container>
    </ModalContainer>
  );
};
